import {
  Sunrise,
  Clock,
  Calendar,
  MapPin,
  Dumbbell,
  Target,
  UserCheck,
  Car,
  MessageCircle,
  Phone,
  CheckCircle2,
} from "lucide-react";
import { ACADEMY } from "../lib/academy-data";
import SectionHeader from "../components/SectionHeader";

const PERKS = [
  {
    icon: Dumbbell,
    title: "עבודה גופנית ממוקדת",
    text: "אימוני כוח, מהירות וזריזות שנבנו במיוחד לשחקני כדורסל צעירים.",
  },
  {
    icon: Target,
    title: "טכניקה אישית ברמה הגבוהה",
    text: "פיתוח יכולות אישיות של כל שחקן — מסירה, כדרור, קליעה והחלטות משחק.",
  },
  {
    icon: UserCheck,
    title: "מעקב אישי צמוד",
    text: "ליווי אישי לאורך כל השנה כדי לוודא שאתם באמת משתפרים.",
  },
];

/**
 * Reusable Morning Academy page — accepts variant config.
 * @param {object} props
 * @param {string} props.testIdPrefix - unique prefix for data-testids
 * @param {string} props.locationName - e.g. "ישיבת נחשון" or "עין כרם"
 * @param {string} props.titleLine1 - big hero line 1
 * @param {string} props.titleLine2Red - big hero line 2 (in red)
 * @param {string} props.days - e.g. "ראשון וחמישי"
 * @param {string} props.hours - e.g. "07:00 – 08:00"
 * @param {string[]} props.audience - array of audience descriptions
 * @param {string} props.whatsappGroupUrl - link to public group chat
 */
export default function MorningAcademyPage({
  testIdPrefix,
  locationName,
  titleLine1,
  titleLine2Red,
  days,
  hours,
  audience,
  whatsappGroupUrl,
}) {
  const details = [
    { icon: Calendar, label: "ימים", value: days },
    { icon: Clock, label: "שעות", value: hours },
    { icon: MapPin, label: "מיקום", value: locationName },
  ];

  const managerWa = `https://wa.me/${ACADEMY.managerWhatsapp}?text=${encodeURIComponent(
    `היי מעיין, אני מעוניין/ת בפרטים נוספים על אקדמיית הבוקר ${locationName}.`
  )}`;

  return (
    <div data-testid={`${testIdPrefix}-page`}>
      {/* Hero */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{
          background:
            "radial-gradient(circle at 15% 40%, rgba(220,38,38,0.22) 0%, transparent 55%), radial-gradient(circle at 90% 90%, rgba(220,38,38,0.15) 0%, transparent 50%), #0A0A0A",
        }}
      >
        <div className="absolute inset-0 diagonal-stripe opacity-30 pointer-events-none" />
        <div className="container mx-auto px-6 md:px-10 relative">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-[#DC2626] text-white px-4 py-2 mb-6">
              <Sunrise size={16} strokeWidth={3} />
              <span className="font-heading text-xs font-bold tracking-widest uppercase">
                אקדמיית בוקר • {locationName}
              </span>
            </div>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight leading-[0.9]">
              {titleLine1}
              <br />
              <span className="text-[#DC2626]">{titleLine2Red}</span>
              <br />
              יוצאת לדרך!
            </h1>
            <p className="font-body text-lg md:text-xl text-neutral-200 mt-6 leading-relaxed max-w-3xl">
              האקדמיה מיועדת במיוחד ל
              <strong className="text-white">שחקני הפועל מטה יהודה</strong> המעוניינים לקחת את
              המשחק שלהם צעד אחד קדימה עם אימוני העשרה מקצועיים.
            </p>

            <div className="flex flex-wrap gap-4 mt-10">
              <a
                href={whatsappGroupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FB957] text-white font-heading font-bold text-base md:text-lg uppercase tracking-wider px-8 py-4 rounded-sm transition-all duration-300 shadow-[0_0_30px_rgba(37,211,102,0.45)] hover:shadow-[0_0_45px_rgba(37,211,102,0.7)] hover:-translate-y-0.5"
                data-testid={`${testIdPrefix}-whatsapp-group-btn`}
              >
                <MessageCircle size={20} strokeWidth={2.5} fill="currentColor" />
                לקבוצת הווטסאפ
              </a>
              <a
                href={managerWa}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white hover:bg-white hover:text-[#0A0A0A] text-white font-heading font-bold text-base md:text-lg uppercase tracking-wider px-8 py-4 rounded-sm transition-all duration-300"
                data-testid={`${testIdPrefix}-details-btn`}
              >
                <Phone size={20} strokeWidth={3} />
                לפרטים נוספים — מעיין
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Details strip */}
      <section
        className="bg-[#050505] border-y border-white/10 py-8"
        data-testid={`${testIdPrefix}-details`}
      >
        <div className="container mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12">
            {details.map((d) => (
              <div
                key={d.label}
                className="flex items-center gap-4 justify-center md:justify-start"
                data-testid={`${testIdPrefix}-detail-${d.label}`}
              >
                <div className="w-14 h-14 inline-flex items-center justify-center bg-[#DC2626]/15 border border-[#DC2626]/40 text-[#DC2626] flex-shrink-0">
                  <d.icon size={24} strokeWidth={2.5} />
                </div>
                <div>
                  <p className="font-heading text-xs font-bold tracking-widest text-neutral-400 uppercase mb-0.5">
                    {d.label}
                  </p>
                  <p className="font-heading text-xl font-black text-white">{d.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="py-24 md:py-28 bg-[#0A0A0A]" data-testid={`${testIdPrefix}-perks`}>
        <div className="container mx-auto px-6 md:px-10">
          <SectionHeader
            kicker="מה מחכה לכם בכל אימון"
            title="להתחיל את היום בכוח"
            description="אקדמיית הבוקר נבנתה כדי להעניק לשחקנים היבט נוסף של אימון — לפני שיום הלימודים בכלל התחיל."
            testId={`${testIdPrefix}-perks-header`}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PERKS.map((p, i) => (
              <div
                key={p.title}
                className="group bg-[#171717] border border-white/10 hover:border-[#DC2626]/60 transition-all duration-500 p-8 md:p-10 relative overflow-hidden"
                data-testid={`${testIdPrefix}-perk-${i}`}
              >
                <div className="absolute top-0 left-0 w-1 h-0 group-hover:h-full bg-[#DC2626] transition-all duration-500" />
                <div className="w-14 h-14 inline-flex items-center justify-center bg-[#DC2626]/15 border border-[#DC2626]/30 text-[#DC2626] mb-6">
                  <p.icon size={26} strokeWidth={2.5} />
                </div>
                <h3 className="font-heading text-2xl font-black text-white mb-3 uppercase">
                  {p.title}
                </h3>
                <p className="font-body text-neutral-400 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who is it for + note */}
      <section className="py-20 md:py-24 bg-[#050505]">
        <div className="container mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="bg-[#171717] border border-white/10 border-r-4 border-r-[#DC2626] p-8 md:p-10">
            <h3 className="font-heading text-3xl md:text-4xl font-black text-white uppercase mb-6">
              למי זה מיועד?
            </h3>
            <ul className="space-y-4">
              {audience.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 font-body text-base md:text-lg text-white"
                >
                  <CheckCircle2
                    size={24}
                    strokeWidth={2.5}
                    className="text-[#DC2626] flex-shrink-0 mt-0.5"
                  />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#DC2626] p-8 md:p-10 relative overflow-hidden">
            <div className="absolute inset-0 diagonal-stripe opacity-30 pointer-events-none" />
            <div className="relative">
              <Car size={40} strokeWidth={2.5} className="text-white mb-4" />
              <h3 className="font-heading text-2xl md:text-3xl font-black text-white uppercase mb-3">
                שימו לב — הגעה עצמאית
              </h3>
              <p className="font-body text-base md:text-lg text-white/95 leading-relaxed">
                ההגעה לאימוני הבוקר הינה <strong>עצמאית</strong>. וודאו מראש שיש לכם דרך להגיע בזמן — האימון מתחיל בדיוק בשעה שנקבעה, וכל דקה חשובה.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Manager WhatsApp + Group */}
      <section className="py-20 md:py-24 bg-[#0A0A0A]" data-testid={`${testIdPrefix}-final-cta`}>
        <div className="container mx-auto px-6 md:px-10">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-[#0F1E13] to-[#0A0A0A] border-2 border-[#25D366]/40 p-8 md:p-12 lg:p-16 text-center relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-[#25D366]/20 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-[#25D366] text-white rounded-full mb-6 shadow-[0_10px_40px_rgba(37,211,102,0.4)]">
                <MessageCircle size={36} strokeWidth={2.5} fill="currentColor" />
              </div>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-[0.95]">
                מוכנים להשקיע
                <br />
                <span className="text-[#25D366]">בעצמכם?</span>
              </h2>
              <p className="font-body text-base md:text-lg text-neutral-300 mt-5 max-w-xl mx-auto">
                הצטרפו לקבוצת הווטסאפ שלנו, או צרו קשר ישיר עם מעיין לפרטים נוספים.
              </p>
              <div className="flex flex-wrap gap-4 justify-center mt-8">
                <a
                  href={whatsappGroupUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1FB957] text-white font-heading font-black text-base md:text-lg uppercase tracking-wider px-8 py-4 rounded-sm transition-all duration-300 shadow-[0_0_40px_rgba(37,211,102,0.55)] hover:shadow-[0_0_60px_rgba(37,211,102,0.85)] hover:-translate-y-0.5"
                  data-testid={`${testIdPrefix}-final-group-btn`}
                >
                  <MessageCircle size={20} strokeWidth={2.5} fill="currentColor" />
                  לקבוצת הווטסאפ
                </a>
                <a
                  href={managerWa}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-transparent border-2 border-white hover:bg-white hover:text-[#0A0A0A] text-white font-heading font-black text-base md:text-lg uppercase tracking-wider px-8 py-4 rounded-sm transition-all duration-300"
                  data-testid={`${testIdPrefix}-final-details-btn`}
                >
                  <Phone size={20} strokeWidth={3} />
                  לפרטים נוספים — מעיין
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
